/**
 * Executive Dashboard - one board-facing screen rolling academics, HR,
 * finance and community metrics into a single "at a glance" view. Nothing
 * else in this app combines modules this way — every other dashboard
 * (Analytics, Strategic Reports, Reports) is scoped to one module at a time.
 *
 * The Overview tab is a customizable widget grid (picker + saved per-user
 * layout, see routers/executive_reports.py's dashboard-layout/
 * available-widgets endpoints) rather than a fixed 6-tile grid — each tile
 * drills into the real detail view on ExecutiveInsightsPage/StrategicReportsPage.
 * The Teacher Effectiveness tab is a separate, unrelated feature (an
 * objective composite score) that lives on this page because there's no
 * dedicated HR/staff-performance page in this app yet.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import executiveReportsService from '../services/executiveReportsService';
import schoolsService from '../services/schoolsService';
import { CircleNotch, Gear, ArrowUp, ArrowDown, X, ArrowsDownUp } from '@phosphor-icons/react';
import './ExecutiveDashboardPage.css';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'teacher-effectiveness', label: 'Teacher Effectiveness' },
];

// Maps a widget's backend `endpoint` key to the executiveReportsService call
// that fetches it — mirrors the AVAILABLE_WIDGETS catalog in
// routers/executive_reports.py. Endpoints that require a parameter this
// page has no sensible default for (payroll-vs-budget needs a fiscal
// period, exam-benchmark-comparison needs an exam name/year) are omitted —
// those widgets are "link" tiles (no field) that just navigate through.
const ENDPOINT_FETCHERS = {
  'kpi-summary': () => executiveReportsService.getKpiSummary(),
  'workforce-analytics': () => executiveReportsService.getWorkforceAnalytics(),
  'pd-roi': () => executiveReportsService.getPdRoi(),
  'capacity-forecast': () => executiveReportsService.getCapacityForecast(),
  'asset-utilization': () => executiveReportsService.getAssetUtilization(),
  'cohort-trends': () => executiveReportsService.getCohortTrends(),
};

function extractWidgetValue(data, field) {
  if (!data || !field) return null;
  if (field === 'cohorts') return Array.isArray(data.cohorts) ? data.cohorts.length : null;
  return data[field];
}

function formatWidgetValue(key, value) {
  if (value === null || value === undefined) return '—';
  if (key === 'fee_overdue_balance') return `GHS ${Number(value).toLocaleString()}`;
  if (key === 'staff_attrition_rate' || key === 'capacity_utilization_pct' || key === 'asset_utilization_pct') return `${value}%`;
  return value;
}

function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    schoolsService.listAllTerms().then((data) => setTerms(data?.items || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="page executive-dashboard-page">
        <div className="page-header">
          <div>
            <h1>Executive Dashboard</h1>
            <p>Academics, HR, finance, and community — one screen for leadership</p>
          </div>
        </div>

        <div className="edp-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab navigate={navigate} />}
        {tab === 'teacher-effectiveness' && <TeacherEffectivenessTab terms={terms} />}
      </div>
    </Layout>
  );
}

/* ---------------------------------------------------------------- */
/* Overview — customizable widget grid                               */
/* ---------------------------------------------------------------- */

function OverviewTab({ navigate }) {
  const [catalog, setCatalog] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({}); // endpoint -> raw response
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);

  const catalogByKey = useMemo(() => Object.fromEntries(catalog.map((w) => [w.key, w])), [catalog]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogRes, layoutRes] = await Promise.all([
        executiveReportsService.getAvailableWidgets(),
        executiveReportsService.getDashboardLayout(),
      ]);
      const widgetCatalog = catalogRes.widgets || [];
      setCatalog(widgetCatalog);
      const layoutWidgets = (layoutRes.widgets || []).filter((k) => widgetCatalog.some((w) => w.key === k));
      setWidgets(layoutWidgets);

      const byKey = Object.fromEntries(widgetCatalog.map((w) => [w.key, w]));
      const endpointsNeeded = [...new Set(layoutWidgets.map((k) => byKey[k]?.endpoint).filter((e) => e && ENDPOINT_FETCHERS[e]))];
      const results = await Promise.all(endpointsNeeded.map((e) => ENDPOINT_FETCHERS[e]().catch(() => null)));
      const dataMap = {};
      endpointsNeeded.forEach((e, i) => { dataMap[e] = results[i]; });
      setWidgetData(dataMap);
      setGeneratedAt(dataMap['kpi-summary']?.generated_at || new Date().toISOString());
    } catch (error) {
      toast.error('Failed to load executive summary: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTileClick = (spec) => {
    if (spec?.route) navigate(spec.route);
  };

  return (
    <div className="edp-panel">
      <div className="edp-toolbar">
        <button className="btn btn-secondary" onClick={load} disabled={loading}>{loading ? 'Refreshing...' : 'Refresh'}</button>
        <button className="btn btn-secondary" onClick={() => setShowCustomize(true)}><Gear size={16} /> Customize</button>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : widgets.length === 0 ? (
        <div className="empty-state"><p>No widgets selected — click Customize to add some.</p></div>
      ) : (
        <div className="edp-grid">
          {widgets.map((key) => {
            const spec = catalogByKey[key];
            if (!spec) return null;
            const raw = spec.endpoint ? widgetData[spec.endpoint] : null;
            const value = spec.field ? extractWidgetValue(raw, spec.field) : null;
            const isLink = !spec.field;
            return (
              <button
                type="button"
                key={key}
                className={`edp-card ${key === 'fee_overdue_balance' ? 'edp-warn' : ''} ${spec.route ? 'edp-clickable' : ''}`}
                onClick={() => handleTileClick(spec)}
              >
                {isLink ? (
                  <span className="edp-link-label">{spec.label} →</span>
                ) : (
                  <>
                    <span className="edp-value">{formatWidgetValue(key, value)}</span>
                    <span className="edp-label">{spec.label}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      {generatedAt && <p className="edp-generated">Generated {new Date(generatedAt).toLocaleString()}</p>}

      {showCustomize && (
        <CustomizeModal
          catalog={catalog}
          initialWidgets={widgets}
          onClose={() => setShowCustomize(false)}
          onSaved={(saved) => { setWidgets(saved); setShowCustomize(false); load(); }}
        />
      )}
    </div>
  );
}

function CustomizeModal({ catalog, initialWidgets, onClose, onSaved }) {
  const [selected, setSelected] = useState(initialWidgets);
  const [saving, setSaving] = useState(false);
  const catalogByKey = useMemo(() => Object.fromEntries(catalog.map((w) => [w.key, w])), [catalog]);

  const toggle = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const move = (index, direction) => {
    setSelected((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await executiveReportsService.saveDashboardLayout(selected);
      toast.success('Dashboard layout saved');
      onSaved(result.widgets || selected);
    } catch (error) {
      toast.error('Failed to save layout: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Customize Dashboard</h2><button className="btn-close" onClick={onClose}><X size={18} /></button></div>

        <div className="edp-customize-body">
          <div className="edp-customize-col">
            <h4>Selected — <ArrowsDownUp size={13} /> reorder with the arrows</h4>
            {selected.length === 0 ? <p className="empty">Nothing selected yet</p> : (
              <ul className="edp-selected-list">
                {selected.map((key, i) => (
                  <li key={key}>
                    <span>{catalogByKey[key]?.label || key}</span>
                    <span className="edp-row-actions">
                      <button type="button" className="btn btn-sm btn-secondary" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={13} /></button>
                      <button type="button" className="btn btn-sm btn-secondary" disabled={i === selected.length - 1} onClick={() => move(i, 1)}><ArrowDown size={13} /></button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => toggle(key)}><X size={13} /></button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="edp-customize-col">
            <h4>Available Widgets</h4>
            <div className="edp-checkbox-grid">
              {catalog.map((w) => (
                <label key={w.key} className="edp-checkbox-item">
                  <input type="checkbox" checked={selected.includes(w.key)} onChange={() => toggle(w.key)} />
                  {w.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Layout'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Teacher Effectiveness                                             */
/* ---------------------------------------------------------------- */

function TeacherEffectivenessTab({ terms }) {
  const [termId, setTermId] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('composite_score');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!termId && terms.length) setTermId(terms.find((t) => t.is_current)?.id || terms[0].id);
  }, [terms, termId]);

  const load = useCallback(async () => {
    if (!termId) return;
    setLoading(true);
    try {
      const data = await executiveReportsService.getTeacherEffectiveness(termId);
      setTeachers(data.teachers || []);
    } catch (error) {
      toast.error('Failed to load teacher effectiveness: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [termId]);

  useEffect(() => { load(); }, [load]);

  const sortBy = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...teachers].sort((a, b) => {
    const get = (t) => (sortKey === 'composite_score' ? t.composite_score
      : sortKey === 'grade' ? t.components.grade_improvement.score
      : sortKey === 'attendance' ? t.components.attendance_rate.score
      : t.components.pd_completion.score);
    const diff = (get(a) ?? 0) - (get(b) ?? 0);
    return sortDir === 'desc' ? -diff : diff;
  });

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <div className="edp-panel">
      <div className="form-group edp-inline-filter">
        <label>Academic Term</label>
        <select value={termId} onChange={(e) => setTermId(e.target.value)}>
          {terms.map((t) => <option key={t.id} value={t.id}>{t.academic_year} — {t.term}</option>)}
        </select>
      </div>
      <p className="edp-hint">Objective score combining grade improvement (40%), attendance rate (30%), and PD completion (30%) for each teacher's classes — no subjective ratings involved.</p>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="empty-state"><p>No timetable entries found for this term</p></div>
      ) : (
        <div className="edp-table">
          <table>
            <thead>
              <tr>
                <th>Teacher</th>
                <th className="edp-sortable" onClick={() => sortBy('composite_score')}>Composite Score{sortIndicator('composite_score')}</th>
                <th className="edp-sortable" onClick={() => sortBy('grade')}>Grade Improvement{sortIndicator('grade')}</th>
                <th className="edp-sortable" onClick={() => sortBy('attendance')}>Attendance Rate{sortIndicator('attendance')}</th>
                <th className="edp-sortable" onClick={() => sortBy('pd')}>PD Completion{sortIndicator('pd')}</th>
                <th>Classes</th>
                <th>Subjects</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.staff_id}>
                  <td>{t.teacher_name}</td>
                  <td><strong>{t.composite_score}</strong></td>
                  <td>{t.components.grade_improvement.score}{t.components.grade_improvement.improvement_pct_points != null ? ` (${t.components.grade_improvement.improvement_pct_points > 0 ? '+' : ''}${t.components.grade_improvement.improvement_pct_points}pp)` : ''}</td>
                  <td>{t.components.attendance_rate.score}</td>
                  <td>{t.components.pd_completion.score} ({t.components.pd_completion.hours_completed}h)</td>
                  <td>{t.classes_taught}</td>
                  <td>{t.subjects_taught}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ExecutiveDashboardPage;
